// Slice B2.3 of docs/planning/activity_dock_workflow_facilitation.md:
// fetch archived leads from the past N days so the activity dock can
// surface a "sent past 30 days" lookback. The dock's in-memory `leads`
// pool only carries draft / unsent / recently-sent leads — once a lead
// is archived via `archive_lead` the row's `data` is nulled and the
// app-side store drops it. This helper queries the persisted
// `archived_summary` jsonb directly.
//
// Auth model: relies on the existing browser Supabase client + its RLS
// policies. Callers must be authenticated; row-level security enforces
// that only leads belonging to the user's organization come back.

import type { SupabaseClient } from "@supabase/supabase-js";

export type RecentSentLeadRow = {
  /** Lead id (uuid). Kept so consumers can link to lead-event drilldowns later. */
  id: string;
  /** Best-effort sent timestamp. Falls back to `updated_at` on rows where the
   * archive happened before `sent_at` was persisted (rare; pre-Slice-B2.3
   * archived rows). */
  sentAtIso: string;
  /** Whether the row was an explicit `sent_at` or an `updated_at` fallback —
   * exposes the data-quality detail to UI consumers that want to mark it
   * (e.g. an "approx." tag) without a second round-trip. */
  sentAtSource: "sent_at" | "updated_at";
  /** Compact summary the archive RPC stamps on the row. Fields can be null. */
  summary: {
    name: string | null;
    product_line: string | null;
    product_subtype: string | null;
    size_config: string | null;
    lead_source: string | null;
    interest_level: string | null;
    timeline: string | null;
    customer_location: string | null;
  };
};

export type FetchRecentSentLeadsArgs = {
  client: SupabaseClient;
  organizationId: string;
  /** Lookback window in days. Negative / zero defaults to 30. */
  days?: number;
  /** Hard cap on returned rows. Defaults to 100. */
  limit?: number;
};

export type FetchRecentSentLeadsResult =
  | { ok: true; rows: RecentSentLeadRow[] }
  | { ok: false; error: string };

const DEFAULT_DAYS = 30;
const DEFAULT_LIMIT = 100;

/**
 * Pure helper: given a single archived-row payload from Supabase, builds
 * the public `RecentSentLeadRow` shape (or returns `null` if the row is
 * missing both `sent_at` and `updated_at`, which would leave us with no
 * orderable timestamp). Exported so the test suite can exercise the
 * normalization without a Supabase client.
 */
export function normalizeArchivedLeadRow(
  input: Record<string, unknown> | null | undefined,
): RecentSentLeadRow | null {
  if (!input || typeof input !== "object") return null;
  const id = input.id;
  if (typeof id !== "string" || id.length === 0) return null;
  const sentAt = typeof input.sent_at === "string" ? input.sent_at : null;
  const updatedAt =
    typeof input.updated_at === "string" ? input.updated_at : null;
  const chosen = sentAt ?? updatedAt;
  if (!chosen) return null;
  return {
    id,
    sentAtIso: chosen,
    sentAtSource: sentAt ? "sent_at" : "updated_at",
    summary: normalizeArchivedSummary(input.archived_summary),
  };
}

function normalizeArchivedSummary(
  raw: unknown,
): RecentSentLeadRow["summary"] {
  const empty: RecentSentLeadRow["summary"] = {
    name: null,
    product_line: null,
    product_subtype: null,
    size_config: null,
    lead_source: null,
    interest_level: null,
    timeline: null,
    customer_location: null,
  };
  if (!raw || typeof raw !== "object") return empty;
  const obj = raw as Record<string, unknown>;
  return {
    name: stringOrNull(obj.name),
    product_line: stringOrNull(obj.product_line),
    product_subtype: stringOrNull(obj.product_subtype),
    size_config: stringOrNull(obj.size_config),
    lead_source: stringOrNull(obj.lead_source),
    interest_level: stringOrNull(obj.interest_level),
    timeline: stringOrNull(obj.timeline),
    customer_location: stringOrNull(obj.customer_location),
  };
}

function stringOrNull(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Returns archived leads for `organizationId` whose `sent_at` (or
 * `updated_at` fallback) lies inside the last `days` days. Excludes
 * test leads. Newest first. Hard-capped at `limit` rows.
 */
export async function fetchRecentSentLeads(
  args: FetchRecentSentLeadsArgs,
): Promise<FetchRecentSentLeadsResult> {
  const { client, organizationId } = args;
  if (!organizationId) {
    return { ok: false, error: "missing organizationId" };
  }
  const days =
    typeof args.days === "number" && args.days > 0 ? args.days : DEFAULT_DAYS;
  const limit =
    typeof args.limit === "number" && args.limit > 0
      ? args.limit
      : DEFAULT_LIMIT;
  const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    .toISOString();

  // Supabase doesn't have a single "coalesce(sent_at, updated_at)"
  // filter syntax, so we use the `or` operator to express the union.
  // Test leads are excluded explicitly so platform spike fixtures
  // never bleed into a salesperson's history.
  const { data, error } = await client
    .from("leads")
    .select("id, sent_at, updated_at, archived_summary, is_test")
    .eq("organization_id", organizationId)
    .eq("status", "archived")
    .eq("is_test", false)
    .or(`sent_at.gte.${cutoff},and(sent_at.is.null,updated_at.gte.${cutoff})`)
    .order("sent_at", { ascending: false, nullsFirst: false })
    .order("updated_at", { ascending: false })
    .limit(limit);

  if (error) {
    return { ok: false, error: error.message };
  }
  const rows = (data ?? [])
    .map((r) =>
      normalizeArchivedLeadRow(r as Record<string, unknown>),
    )
    .filter((r): r is RecentSentLeadRow => r !== null);
  return { ok: true, rows };
}
